import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  getNotificationsList,
  getUnreadNotificationsCount,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from '../../lib/api/notifications';
import { parseNotificationTarget } from '../../lib/notifications';
import './NotificationBell.css';

// SVG Icons
const BellIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const CheckAllIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CloseIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function formatNotificationTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timePart = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Сегодня, ${timePart}`;
  if (isYesterday) return `Вчера, ${timePart}`;
  return `${date.toLocaleDateString([], { day: '2-digit', month: '2-digit' })}, ${timePart}`;
}

export default function NotificationBell({ profile, socket, onNavigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [dropStyle, setDropStyle] = useState({});

  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const isMountedRef = useRef(true);

  const calcPosition = useCallback(() => {
    if (!buttonRef.current) return {};
    if (window.innerWidth <= 768) {
      return {};
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const isSidebar = rect.left < 300;

    if (isSidebar) {
      const top = Math.max(16, Math.min(rect.top - 8, window.innerHeight - 490));
      const left = Math.min(rect.right + 14, window.innerWidth - 396);
      return {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        right: 'auto',
        bottom: 'auto',
        zIndex: 1050,
      };
    } else {
      const top = rect.bottom + 8;
      const right = Math.max(12, window.innerWidth - rect.right);
      return {
        position: 'fixed',
        top: `${top}px`,
        right: `${right}px`,
        left: 'auto',
        bottom: 'auto',
        zIndex: 1050,
      };
    }
  }, []);

  const updatePosition = useCallback(() => {
    setDropStyle(calcPosition());
  }, [calcPosition]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  const handleToggle = () => {
    if (!isOpen) {
      setDropStyle(calcPosition());
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Загрузка счетчика при старте
  const loadUnreadCount = useCallback(async () => {
    if (!profile?.id) return;
    const count = await getUnreadNotificationsCount();
    if (isMountedRef.current) {
      setUnreadCount(typeof count === 'number' ? count : 0);
    }
  }, [profile?.id]);

  // Загрузка списка уведомлений
  const loadNotifications = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const items = await getNotificationsList();
      if (isMountedRef.current) {
        setNotifications(Array.isArray(items) ? items : []);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [profile?.id]);

  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  // При открытии панели подгружаем свежий список
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [isOpen, loadNotifications, loadUnreadCount]);

  // Слушатель WebSocket события 'notification'
  useEffect(() => {
    if (!socket || !profile?.id) return;

    const handleNewNotification = (newRecord) => {
      if (!newRecord || !newRecord.id) return;
      // Проверяем, что уведомление адресовано текущему пользователю
      if (newRecord.profile_id && newRecord.profile_id !== profile.id) return;

      setNotifications((prev) => {
        // Дедупликация по ID
        if (prev.some((n) => n.id === newRecord.id)) return prev;
        return [newRecord, ...prev.slice(0, 49)];
      });

      if (!newRecord.is_read) {
        setUnreadCount((prev) => prev + 1);
      }
    };

    socket.on('notification', handleNewNotification);
    return () => {
      socket.off('notification', handleNewNotification);
    };
  }, [socket, profile?.id]);

  // Закрытие при клике вне компонента или по нажатию Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && containerRef.current.contains(e.target)) {
        return;
      }
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Клик по конкретному уведомлению
  const handleItemClick = async (item) => {
    if (!item) return;

    // 1. Если не прочитано — отмечаем на сервере и локально
    if (!item.is_read) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      markNotificationAsRead(item.id).catch(() => {});
    }

    // 2. Выполняем SPA навигацию по data, если задана
    const data = item.data;
    if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      const target = parseNotificationTarget(data);
      if (target && target.page && onNavigate) {
        setIsOpen(false);
        onNavigate(target.page, {
          ...(target.queryParams || {}),
          tab: target.targetTab,
          targetType: target.targetType,
          targetId: target.targetId
        });
      }
    }
  };

  // Отметить всё как прочитанное
  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    if (markingAll || unreadCount === 0) return;
    setMarkingAll(true);
    try {
      const res = await markAllNotificationsAsRead();
      if (res && res.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } finally {
      if (isMountedRef.current) {
        setMarkingAll(false);
      }
    }
  };

  const badgeText = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <div className="notification-bell-container" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`btn btn-icon btn-ghost notification-bell-btn ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        aria-label="Центр уведомлений"
        aria-expanded={isOpen}
        title="Уведомления"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="notification-badge" aria-label={`Непрочитанных: ${unreadCount}`}>
            {badgeText}
          </span>
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="notification-dropdown"
            style={dropStyle}
            role="region"
            aria-label="Список уведомлений"
          >
            <div className="notification-header">
              <div className="notification-title-row">
                <h3 className="notification-header-title">Уведомления</h3>
                {unreadCount > 0 && (
                  <span className="notification-header-count">{unreadCount} новых</span>
                )}
              </div>
              <div className="notification-header-actions">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost notification-read-all-btn"
                    onClick={handleMarkAllRead}
                    disabled={markingAll}
                  >
                    <CheckAllIcon />
                    <span>Прочитать всё</span>
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-icon btn-ghost notification-close-btn"
                  onClick={() => setIsOpen(false)}
                  aria-label="Закрыть"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className="notification-list" tabIndex={0}>
              {loading && notifications.length === 0 ? (
                <div className="notification-loading">Загрузка уведомлений...</div>
              ) : notifications.length === 0 ? (
                <div className="notification-empty">
                  <div className="notification-empty-icon">
                    <BellIcon size={32} />
                  </div>
                  <div className="notification-empty-title">Нет уведомлений</div>
                  <div className="notification-empty-desc">
                    Здесь будут отображаться важные события смен и системы
                  </div>
                </div>
              ) : (
                notifications.map((item) => {
                  const isUnread = !item.is_read;
                  const hasAction = Boolean(item.data && (item.data.url || item.data.navType));
                  return (
                    <div
                      key={item.id}
                      className={`notification-item ${isUnread ? 'unread' : ''} ${hasAction ? 'clickable' : ''}`}
                      onClick={() => handleItemClick(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleItemClick(item);
                        }
                      }}
                    >
                      <div className="notification-item-indicator" />
                      <div className="notification-item-content">
                        <div className="notification-item-top">
                          <span className="notification-item-title">{item.title || 'Уведомление'}</span>
                          <span className="notification-item-time">
                            {formatNotificationTime(item.created_at)}
                          </span>
                        </div>
                        {item.message && (
                          <p className="notification-item-message">{item.message}</p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
